import { Tabs, Button, Spin, Tag, Dropdown } from "antd";
import { FC, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MoreVertical, Zap, FileText, Check, X } from "lucide-react";
import { useGetEstimate } from "~/services/estimate";
import { useEstimateDetailStore, EstimateTab } from "./estimate-detail.store";
import { EstimateStatus } from "~/types/estimate";
import { OverviewTab } from "./tabs/overview";
import { ServicesTab } from "./tabs/services";
import { InventoryTab } from "./tabs/inventory";
import { NotesTab } from "./tabs/notes";
import { BillingTab } from "./tabs/billing";
import { ServiceFormModal } from "./service-form-modal";
import { InventoryTemplateModal } from "./inventory-template-modal";

export const EstimateDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeTab, setActiveTab, setEstimate, showInventoryModal, setShowInventoryModal } = useEstimateDetailStore();

  const { data: estimate, isLoading, refetch } = useGetEstimate(id!);

  useEffect(() => {
    console.log("🎯 EstimateDetail useEffect - estimate changed:");
    console.log("  - ID:", estimate?.id);
    console.log("  - Services count:", estimate?.services?.length);
    console.log("  - Services:", estimate?.services);

    if (estimate) {
      setEstimate(estimate);
    }
  }, [estimate, setEstimate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <FileText size={48} className="text-gray-400" />
        <p className="text-gray-500">Estimate not found</p>
        <Button onClick={() => navigate("/estimates")}>Back to Estimates</Button>
      </div>
    );
  }

  const getStatusBadge = (status: EstimateStatus) => {
    const statusConfig = {
      [EstimateStatus.DRAFT]: { color: "default", text: "Draft" },
      [EstimateStatus.SENT]: { color: "blue", text: "Sent" },
      [EstimateStatus.ACCEPTED]: { color: "green", text: "Accepted" },
      [EstimateStatus.REJECTED]: { color: "red", text: "Rejected" },
      [EstimateStatus.CONVERTED]: { color: "purple", text: "Converted" },
    };

    const config = statusConfig[status];
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const menuItems = [
    {
      key: "edit",
      label: "Edit Estimate",
      disabled: estimate.status === EstimateStatus.CONVERTED,
    },
    {
      key: "duplicate",
      label: "Duplicate Estimate",
    },
    {
      key: "delete",
      label: "Delete Estimate",
      danger: true,
      disabled: estimate.status === EstimateStatus.CONVERTED,
    },
  ];

  const tabItems = [
    {
      key: EstimateTab.OVERVIEW,
      label: "Overview",
      children: <OverviewTab estimate={estimate} />,
    },
    {
      key: EstimateTab.SERVICES,
      label: `Services (${estimate?.services?.length || 0})`,
      children: <ServicesTab estimate={estimate} />,
    },
    {
      key: EstimateTab.INVENTORY,
      label: "Inventory",
      children: <InventoryTab estimate={estimate} />,
    },
    {
      key: EstimateTab.NOTES,
      label: "Notes",
      children: <NotesTab estimate={estimate} />,
    },
    {
      key: EstimateTab.BILLING,
      label: "Billing",
      children: <BillingTab estimate={estimate} />,
    },
  ];

  const getClientName = () => {
    if (estimate.tempClientData) {
      return estimate.tempClientData.name;
    }
    if (estimate.client) {
      return estimate.client.companyName ||
             `${estimate.client.firstName} ${estimate.client.lastName}`.trim() ||
             "Unnamed Client";
    }
    return "No Client";
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <Button
              icon={<ArrowLeft size={16} />}
              onClick={() => navigate("/estimates")}
            >
              Back
            </Button>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-semibold">{estimate.referenceNumber}</h1>
                {getStatusBadge(estimate.status)}
                {estimate.tempClientData && (
                  <Tag color="orange" icon={<Zap size={12} />}>Quick Estimate</Tag>
                )}
              </div>
              <p className="text-gray-500 mt-1">{getClientName()}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Dropdown menu={{ items: menuItems }} placement="bottomRight">
              <Button icon={<MoreVertical size={16} />} />
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as EstimateTab)}
        items={tabItems}
      />

      {/* Modals */}
      <ServiceFormModal />
      <InventoryTemplateModal
        open={showInventoryModal}
        onClose={() => setShowInventoryModal(false)}
        onAddItems={(items) => {
          console.log("Selected items with packing:", items);
          // TODO: Add items to estimate service or create packing line items
        }}
      />
    </div>
  );
};
